import React, {FC, useCallback, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {OkCancelDialog} from '../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {CommentComposer, CommentComposerPublicProps} from './commentComposer';

/*
 * Props.
 */

interface EditCommentComposerProps
  extends Pick<
    CommentComposerPublicProps,
    | 'panelId'
    | 'addAttachment'
    | 'attachments'
    | 'company'
    | 'user'
    | 'conversation'
    | 'comment'
    | 'endCommentEdit'
    | 'mentionableAliases'
    | 'onRemoveAttachmentClick'
    | 'onTextChange'
    | 'referencedActivityId'
    | 'updateReferencedActivity'
    | 'annotation'
    | 'emojiValidationMap'
    | 'cellsContext'
  > {
  onDeleteClick: () => void;
}

/*
 * Component.
 */

export const EditCommentComposer: FC<EditCommentComposerProps> = (props) => {
  const {attachments, onTextChange, onDeleteClick} = props;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isAttachmentsEmpty = attachments.length === 0;

  /*
   * Event handlers.
   */

  const onComposerTextChange = useCallback(
    (newText: string) => {
      if (!newText && isAttachmentsEmpty) {
        setIsDeleteDialogOpen(true);
        return;
      }

      onTextChange(newText);
    },
    [setIsDeleteDialogOpen, onTextChange, isAttachmentsEmpty],
  );

  /*
   * Render.
   */

  return (
    <>
      {isDeleteDialogOpen && (
        <OkCancelDialog
          title={<Trans id="inline-comment-delete-title">Delete comment</Trans>}
          okButtonLabel={<Trans id="inline-comment-delete-ok">Delete</Trans>}
          onOkClick={onDeleteClick}
          onRequestClose={() => setIsDeleteDialogOpen(false)}
        >
          <Trans id="inline-comment-delete-content">
            Are you sure you would like to delete this comment?
          </Trans>
        </OkCancelDialog>
      )}

      <CommentComposer
        addAttachment={props.addAttachment}
        attachments={props.attachments}
        comment={props.comment}
        annotation={props.annotation}
        company={props.company}
        conversation={props.conversation}
        endCommentEdit={props.endCommentEdit}
        mentionableAliases={props.mentionableAliases}
        onRemoveAttachmentClick={props.onRemoveAttachmentClick}
        onTextChange={onComposerTextChange}
        panelId={props.panelId}
        referencedActivityId={props.referencedActivityId}
        updateReferencedActivity={props.updateReferencedActivity}
        user={props.user}
        // Functionality that is not supported for editing a comment
        editLastComment={() => {}}
        shouldHideExpandButton
        isExpanded={false}
        onExpandedToggle={() => {}}
        sendComment={() => Promise.resolve()}
        emojiValidationMap={props.emojiValidationMap}
        shouldFocus
        isEditing
        cellsContext={props.cellsContext}
      />
    </>
  );
};
